import { FC, memo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useMutation } from 'react-query';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { useStore } from 'hooks/useRootStore';
import { Button } from 'components/Core/Button';
import { tableActionsButtonsTranslations } from './translations';
import { TableActionsButtonsProps, TableActionsButtonsTransFields } from './types';
import styles from './tableActionsButtons.module.scss';

export const TableActionsButtons: FC<TableActionsButtonsProps> = memo(
  ({ id, entity, status, classNameProp, isApproveDisabled = false }) => {
    const {
      requestsStore: { manageRequest },
      popUpStore: { openPopUp, closePopUp },
    } = useStore();

    const { t } = useMyTranslations<TableActionsButtonsTransFields>([tableActionsButtonsTranslations.name]);

    const navigate = useNavigate();

    const { mutate, isLoading } = useMutation(manageRequest, {
      mutationKey: 'manageRequest',
      onMutate: () => {
        closePopUp();
      },
      onSuccess: () => {
        navigate(`/requests/${entity}`);
        if (entity === 'catalogues') {
          localStorage.removeItem(id);
        }
      },
    });

    const handleApprove = useCallback(() => {
      mutate({ id, entity, adminMessage: '', status: null });
    }, [id, entity]);

    return (
      <div className={`${styles.actionBoxBtn} ${classNameProp}`}>
        <Button
          title={t('decline')}
          onClick={() => {
            openPopUp({
              formName: 'request',
              title: t('confirmationTitle'),
              description: t('decline-text'),
              buttonLabel: t('decline'),
              cb: (adminMessage: string) => mutate({ id, entity, adminMessage, status: 'Declined' }),
            });
          }}
          classNameProp={`${styles.button} ${styles.declineButton}`}
          isDisabled={status !== 'Pending' || isLoading}
        />
        <Button
          title={t('approve')}
          onClick={() =>
            openPopUp({
              title: t('confirmationTitle'),
              description: t('approve-text'),
              buttonLabel: t('confirm'),
              formName: 'confirmation',
              cb: handleApprove,
            })
          }
          classNameProp={`${styles.button}`}
          isDisabled={status !== 'Pending' || isLoading || isApproveDisabled}
        />
        <Button
          title={'Revise'}
          onClick={() => {
            openPopUp({
              formName: 'request',
              title: t('confirmationTitle'),
              description: t('revise-text'),
              buttonLabel: t('revise'),
              cb: (adminMessage: string) => mutate({ id, entity, adminMessage, status: 'Revise' }),
            });
          }}
          classNameProp={`${styles.button}`}
          isDisabled={status !== 'Pending' || isLoading}
        />
      </div>
    );
  }
);
